import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import type { DataNode } from 'components/WorkspaceEditor/types'

interface ShouldHideElementOptions {
    /** Whether the Drawing Designation feature is enabled */
    hasDrawingDesignation: boolean
    /** The parent node containing this content item */
    node?: DataNode
    /** The content item to evaluate */
    contentItem: Partial<TextContentElement>
    /** Whether this is the first content item in the PT node */
    isPTFirstItem: boolean
    /** Whether the component is in edit mode */
    isEditMode: boolean
}

/**
 * Determines if a PlainText element should be hidden in Drawing Designation mode.
 *
 * @param options Configuration options for visibility calculation
 * @returns true if the element should be hidden, false otherwise
 */
export const shouldHideElement = ({
    contentItem,
    hasDrawingDesignation,
    isEditMode,
    isPTFirstItem,
    node,
}: ShouldHideElementOptions) => {
    const isFeatureEnabled = hasDrawingDesignation

    // Only apply to Product Type nodes with content items
    const isProductTypeWithContent = Boolean(
        node?.isProductType && node?.contentItemNodes?.[0]
    )

    // Don't hide the first element (it shows the DesignationField)
    const isNotFirstElement = !isPTFirstItem

    // Only hide structural text elements (Text/FieldRef), not OptionGroups or other types
    const isHideableElementType =
        contentItem.type === 'Text' || contentItem.type === 'FieldRef'

    // Don't hide when actively editing
    const isNotEditing = !isEditMode

    // Calculate positions to determine if element is before first OptionGroup
    const currentIndex =
        node?.contentItemNodes?.findIndex((n) => n.id === contentItem.id) ?? -1

    const firstOptionGroupIndex =
        node?.contentItemNodes?.findIndex((n) => n.type === 'OptionGroup') ?? -1

    // Only hide elements that appear before the first OptionGroup
    // (elements after OptionGroups should remain visible)
    const isBeforeFirstOptionGroup =
        firstOptionGroupIndex !== -1 &&
        currentIndex !== -1 &&
        currentIndex < firstOptionGroupIndex

    return (
        isFeatureEnabled &&
        isProductTypeWithContent &&
        isNotFirstElement &&
        isHideableElementType &&
        isNotEditing &&
        isBeforeFirstOptionGroup
    )
}
